import { SurveyResponse } from '../types';

const QUEUE_KEY = 'survey_submission_queue';
const LOCK_KEY = 'survey_queue_lock';

export interface QueuedSubmission {
  id: string;
  response: SurveyResponse;
  attempts: number;
  queuedAt: string;
}

/**
 * Acquire a lock to prevent concurrent queue operations
 */
export function acquireLock(): boolean {
  const lockTimestamp = localStorage.getItem(LOCK_KEY);
  const now = Date.now();

  // If lock exists and is less than 10 seconds old, queue is locked
  if (lockTimestamp && now - parseInt(lockTimestamp) < 10000) {
    return false;
  }

  // Acquire lock
  localStorage.setItem(LOCK_KEY, now.toString());
  return true;
}

/**
 * Release the lock
 */
export function releaseLock(): void {
  localStorage.removeItem(LOCK_KEY);
}

/**
 * Get all queued submissions from localStorage
 */
export function getQueuedSubmissions(): QueuedSubmission[] {
  try {
    const stored = localStorage.getItem(QUEUE_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch (error) {
    console.error('Error reading submission queue:', error);
    return [];
  }
}

/**
 * Add a submission to the queue
 */
export function queueSubmission(response: SurveyResponse): void {
  try {
    const queue = getQueuedSubmissions();
    const newSubmission: QueuedSubmission = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      response: {
        ...response,
        timestamp: response.timestamp instanceof Date
          ? response.timestamp
          : new Date(response.timestamp),
      },
      attempts: 0,
      queuedAt: new Date().toISOString(),
    };
    queue.push(newSubmission);
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    console.log('Queued submission:', newSubmission.id);
  } catch (error) {
    console.error('Error queueing submission:', error);
  }
}

/**
 * Remove a submission from the queue
 */
export function removeFromQueue(id: string): void {
  try {
    const queue = getQueuedSubmissions();
    const filtered = queue.filter(item => item.id !== id);
    localStorage.setItem(QUEUE_KEY, JSON.stringify(filtered));
    console.log('Removed from queue:', id);
  } catch (error) {
    console.error('Error removing from queue:', error);
  }
}

/**
 * Increment attempt counter for a submission
 */
export function incrementAttempts(id: string): void {
  try {
    const queue = getQueuedSubmissions();
    const updated = queue.map(item =>
      item.id === id
        ? { ...item, attempts: item.attempts + 1 }
        : item
    );
    localStorage.setItem(QUEUE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error('Error incrementing attempts:', error);
  }
}

/**
 * Clear all queued submissions
 */
export function clearQueue(): void {
  try {
    localStorage.removeItem(QUEUE_KEY);
    console.log('Queue cleared');
  } catch (error) {
    console.error('Error clearing queue:', error);
  }
}
